/**
 * Integrytas Group - Common Security Module (CSM)
 * usePageSync - calls csm_sync_page_master once when an admin signs in.
 *
 * Non-admins skip the sync (the RPC would refuse anyway). Errors are
 * captured silently and exposed via the returned status so the host UI
 * can decide whether to surface them.
 */

import { useEffect, useState } from 'react';
import { apiDb } from './api_client';
import type { AuthSession } from '../types/auth';
import { PAGE_REGISTRY } from '../pages/page_registry';

export interface PageSyncStatus {
  ran: boolean;
  inserted: number;
  updated: number;
  deactivated: number;
  error: string | null;
}

const INITIAL: PageSyncStatus = {
  ran: false, inserted: 0, updated: 0, deactivated: 0, error: null,
};

export function usePageSync(session: AuthSession | null): PageSyncStatus {
  const [status, setStatus] = useState<PageSyncStatus>(INITIAL);

  useEffect(() => {
    if (!session) return;

    // Only proceed for admins. The trigger-seeded SUPER_ADMIN has is_admin='Y'
    // which the loader exposes by granting wildcard page access; we use the
    // role list as a secondary check for downstream non-bootstrap admins.
    const isAdmin =
      session.effective_user.page_access.length > 0 &&
      session.effective_user.page_access.every(p => p.can_view && p.can_change);
    if (!isAdmin) return;

    let cancelled = false;
    void (async () => {
      const { data, error } = await apiDb.rpc('csm_sync_page_master', {
        p_pages: PAGE_REGISTRY,
      });
      if (cancelled) return;

      if (error) {
        setStatus({ ...INITIAL, ran: true, error: error.message });
        return;
      }
      const row = Array.isArray(data) ? data[0] : data;
      setStatus({
        ran: true,
        inserted:    row?.inserted_count    ?? 0,
        updated:     row?.updated_count     ?? 0,
        deactivated: row?.deactivated_count ?? 0,
        error: null,
      });
    })();

    return () => { cancelled = true; };
    // Keyed on user_id only: the page sync must run once per signed-in admin, not
    // on every new session object identity. `session` is read inside but is stable
    // for a given user_id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.effective_user.user_id]);

  return status;
}
